// Loading overlay shown while games are being imported/synced. There is no
// real progress percentage from the backend (useSyncGamesApi is a single
// mutation, not a stream of updates), so the progress bar is a decorative
// indeterminate sweep rather than a fabricated count — same honesty as the
// original screen, which showed no feedback at all during this wait.
// Design source: Claude Design "Persona Dashboard Redesign" —
// OnboardingLoaderContent.dc.html.

import React, {useEffect, useRef, useState} from 'react';
import {Animated, Modal, View} from 'react-native';
import CCText from '@/Atoms/CCText';
import CCColors from '@/Utils/CCColors';

const QUOTES = [
  {author: 'Savielly Tartakower', quote: 'The blunders are all there on the board, waiting to be made.'},
  {author: 'Jose Capablanca', quote: 'A book cannot by itself teach how to play. It can only serve as a guide.'},
  {author: 'Bobby Fischer', quote: 'All I want to do, ever, is just play Chess.'},
];

interface ImportProgressLoaderProps {
  visible: boolean;
}

export default function ImportProgressLoader({visible}: ImportProgressLoaderProps) {
  const [quoteIndex, setQuoteIndex] = useState(0);
  const sweep = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    if (!visible) return;
    const quoteTimer = setInterval(() => {
      setQuoteIndex(i => (i + 1) % QUOTES.length);
    }, 2200);
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(sweep, {toValue: 1, duration: 1400, useNativeDriver: true}),
        Animated.timing(sweep, {toValue: 0, duration: 0, useNativeDriver: true}),
      ]),
    );
    loop.start();
    return () => {
      clearInterval(quoteTimer);
      loop.stop();
    };
  }, [visible, sweep]);

  if (!visible) return null;

  const quote = QUOTES[quoteIndex];
  const translateX = sweep.interpolate({
    inputRange: [0, 1],
    outputRange: ['-60%', '160%'],
  });

  return (
    <Modal transparent animationType="fade" visible={visible}>
      <View style={styles.backdrop}>
        <View style={styles.card}>
          <CCText
            fontWeight="thunder"
            fontSize={28}
            color={CCColors.Login.Ink}
            style={{textAlign: 'center'}}>
            Fasten your seatbelts!
          </CCText>
          <CCText
            fontSize={14}
            color={CCColors.TextColor.Grey}
            style={{textAlign: 'center', marginTop: 8}}>
            Please wait while we personalize your experience
          </CCText>

          <View style={styles.track}>
            <Animated.View
              style={[styles.fill, {transform: [{translateX}]}]}
            />
          </View>

          <View style={styles.quoteBlock}>
            <CCText
              fontSize={14.5}
              color={CCColors.TextColor.Primary}
              style={{textAlign: 'center', lineHeight: 21}}>
              "{quote.quote}"
            </CCText>
            <CCText
              fontSize={11.5}
              color={CCColors.TextColor.Grey}
              style={{textAlign: 'center', marginTop: 10}}>
              — {quote.author}
            </CCText>
          </View>
        </View>
      </View>
    </Modal>
  );
}

const styles = {
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(38,35,34,0.45)',
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
    padding: 24,
  },
  card: {
    width: '100%' as const,
    maxWidth: 420,
    backgroundColor: CCColors.White,
    borderRadius: 20,
    padding: 32,
  },
  track: {
    height: 8,
    borderRadius: 99,
    backgroundColor: CCColors.Login.Border,
    overflow: 'hidden' as const,
    marginTop: 28,
  },
  fill: {
    width: '40%',
    height: '100%',
    borderRadius: 99,
    backgroundColor: CCColors.Login.GoldDeep,
  },
  quoteBlock: {
    borderTopWidth: 1,
    borderTopColor: CCColors.Login.Border,
    marginTop: 28,
    paddingTop: 20,
  },
};
